import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Users, Ship, FileText, Wallet, Settings, ArrowRight, LayoutDashboard,
  Package, ClipboardCheck, Loader2, MapPin,
} from 'lucide-react';
import { api } from '../contexts/AuthContext';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/erp', icon: LayoutDashboard },
  { label: 'Customers', path: '/erp/customers', icon: Users },
  { label: 'Shipments', path: '/erp/shipments', icon: Ship },
  { label: 'Approvals', path: '/erp/approvals', icon: ClipboardCheck },
  { label: 'Finance', path: '/erp/finance', icon: Wallet },
  { label: 'Settings', path: '/erp/settings', icon: Settings },
];

const CATEGORY_META = {
  IMPORT: { label: 'Import', path: '/erp/shipments?tab=imports' },
  EXPORT: { label: 'Export', path: '/erp/shipments?tab=exports' },
  ADAM_CUSTOMER: { label: 'Adam', path: '/erp/shipments?tab=adam' },
  CHINA_IMPORT: { label: 'China', path: '/erp/shipments?tab=china' },
  FINANCIAL: { label: 'Invoice', path: '/erp/shipments?tab=invoices' },
  APPROVAL: { label: 'Approval', path: '/erp/approvals' },
};

export default function CommandPalette({ onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ customers: [], records: [], tracking: [] });
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setResults({ customers: [], records: [], tracking: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setResults(r.data);
      } catch { setResults({ customers: [], records: [], tracking: [] }); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const go = (path) => { navigate(path); onClose(); };

  const navFiltered = q.trim()
    ? NAV_ITEMS.filter((n) => n.label.toLowerCase().includes(q.trim().toLowerCase()))
    : NAV_ITEMS;

  const flat = [
    ...navFiltered.map((n) => ({ type: 'nav', path: n.path, item: n })),
    ...results.customers.map((c) => ({ type: 'customer', path: `/erp/customers/${c.id}`, item: c })),
    ...results.records.map((r) => ({ type: 'record', path: (CATEGORY_META[r.category] || CATEGORY_META.IMPORT).path, item: r })),
    ...results.tracking.map((s) => ({ type: 'tracking', path: '/erp/shipments', item: s })),
  ];

  useEffect(() => { setActive(0); }, [q, results]);

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && flat[active]) { go(flat[active].path); }
  };

  let idx = -1;
  const Item = ({ children, path, testid }) => {
    idx += 1;
    const i = idx;
    return (
      <button
        onClick={() => go(path)}
        onMouseEnter={() => setActive(i)}
        className="gg-cmdk-item w-full text-start"
        style={active === i ? { background: 'var(--gg-sidebar-active-bg)' } : undefined}
        data-testid={testid}
      >
        {children}
      </button>
    );
  };

  const hasResults = results.customers.length || results.records.length || results.tracking.length;

  return (
    <div className="gg-cmdk-overlay flex items-start justify-center pt-[12vh]" onClick={onClose} data-testid="command-palette">
      <div className="gg-cmdk-panel" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b" style={{ borderColor: 'var(--gg-border)' }}>
          {loading
            ? <Loader2 size={16} className="ms-5 animate-spin" style={{ color: 'var(--gg-accent)' }} />
            : <Search size={16} className="ms-5" style={{ color: 'var(--gg-text-secondary)' }} />}
          <input
            ref={inputRef}
            className="gg-cmdk-input"
            placeholder="Search customer, tracking, file number, invoice…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
            autoFocus
            data-testid="palette-input"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2" style={{ maxHeight: '55vh' }}>
          {q.trim().length >= 2 && !loading && !hasResults && navFiltered.length === 0 && (
            <p className="px-4 py-8 text-center text-sm" style={{ color: 'var(--gg-text-secondary)' }} data-testid="palette-empty">
              No results for &ldquo;{q}&rdquo;
            </p>
          )}

          {navFiltered.length > 0 && (
            <>
              <p className="gg-eyebrow px-3 pt-2 pb-1">Navigate</p>
              {navFiltered.map((n) => (
                <Item key={n.path} path={n.path}>
                  <n.icon size={14} style={{ color: 'var(--gg-text-secondary)' }} />
                  <span className="flex-1">{n.label}</span>
                  <ArrowRight size={12} style={{ color: 'var(--gg-text-secondary)' }} />
                </Item>
              ))}
            </>
          )}

          {results.customers.length > 0 && (
            <>
              <p className="gg-eyebrow px-3 pt-3 pb-1">Customers</p>
              {results.customers.map((c) => (
                <Item key={c.id} path={`/erp/customers/${c.id}`} testid={`palette-customer-${c.id}`}>
                  <Users size={14} style={{ color: 'var(--gg-accent)' }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{c.name}</p>
                    <p className="text-[11px] truncate" style={{ color: 'var(--gg-text-secondary)' }}>
                      {[c.company, c.phone, c.email].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <ArrowRight size={12} style={{ color: 'var(--gg-text-secondary)' }} />
                </Item>
              ))}
            </>
          )}

          {results.records.length > 0 && (
            <>
              <p className="gg-eyebrow px-3 pt-3 pb-1">Records</p>
              {results.records.map((r) => {
                const meta = CATEGORY_META[r.category] || CATEGORY_META.IMPORT;
                return (
                  <Item key={r.id} path={meta.path} testid={`palette-record-${r.id}`}>
                    {r.category === 'APPROVAL'
                      ? <ClipboardCheck size={14} style={{ color: 'var(--gg-info, #2E5E9E)' }} />
                      : <Package size={14} style={{ color: '#2E5E9E' }} />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {r.customerName || '—'}
                        <span className="ms-2 gg-tabular text-[11px]" style={{ color: 'var(--gg-text-secondary)' }}>
                          {r.trackingNumber || r.invoiceNumber || r.approvalNumber || r.fileNumber || ''}
                        </span>
                      </p>
                      <p className="text-[11px] truncate" style={{ color: 'var(--gg-text-secondary)' }}>
                        {[meta.label, r.description, r.fileNumber && `File ${r.fileNumber}`, r.month].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <span className="gg-chip gg-chip-neutral" style={{ fontSize: 9 }}>{meta.label}</span>
                  </Item>
                );
              })}
            </>
          )}

          {results.tracking.length > 0 && (
            <>
              <p className="gg-eyebrow px-3 pt-3 pb-1">Tracked shipments</p>
              {results.tracking.map((s) => (
                <Item key={s.id} path="/erp/shipments" testid={`palette-tracking-${s.id}`}>
                  <MapPin size={14} style={{ color: 'var(--gg-emerald)' }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate gg-tabular">{s.trackingNumber || '(no tracking)'}</p>
                    <p className="text-[11px] truncate" style={{ color: 'var(--gg-text-secondary)' }}>
                      {[s.owner, s.carrier, s.status].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <ArrowRight size={12} style={{ color: 'var(--gg-text-secondary)' }} />
                </Item>
              ))}
            </>
          )}
        </div>
        <div className="px-4 py-2 text-[11px] flex items-center justify-between" style={{ borderTop: '1px solid var(--gg-border)', color: 'var(--gg-text-secondary)' }}>
          <span>↑↓ navigate · Enter open · Esc close</span>
          <span style={{ color: 'var(--gg-accent)' }}>Gerizim</span>
        </div>
      </div>
    </div>
  );
}
